import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import qs from 'qs';
import { moduleRegistry } from './moduleRegistry';
import { gatewayClientConfig } from '@/services/http-client';

/** Module relay client plus a helper for the relay's named-webhook endpoint. */
export interface ModuleHttpClient extends AxiosInstance {
  /**
   * Calls a webhook declared in the module config (`/__webhooks/:name`); the relay resolves
   * its URL and token server-side so they never reach the browser.
   */
  callWebhook<TResponse = unknown>(webhookName: string, data: unknown): Promise<AxiosResponse<TResponse>>;
}

/**
 * Awaits an axios request and returns its response body.
 */
export async function unwrap<T>(request: Promise<AxiosResponse<T>>): Promise<T> {
  const response = await request;
  return response.data;
}

/**
 * Create a relay-backed axios instance for a module.
 *
 * All requests are routed through the Hono BFF at
 * `/server/modules/:moduleId/*` so that API tokens and internal URLs
 * never leave the server.
 *
 * @param moduleId - The module's unique ID (must match the `id` in its definition)
 */
export function createModuleHttpClient(moduleId: string): ModuleHttpClient {
  const client = axios.create({
    headers: gatewayClientConfig.headers,
    withCredentials: gatewayClientConfig.withCredentials,
    paramsSerializer: {
      serialize: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
    },
  });

  client.interceptors.request.use((config) => {
    const instance = moduleRegistry.getInstance(moduleId);

    if (!config.baseURL) {
      config.baseURL = `/server/modules/${moduleId}`;
    }

    const clientId = instance?.clientConfig?.clientId;
    if (clientId) {
      config.headers['X-Client-Id'] = clientId;
    } else if (import.meta.env.DEV && typeof window !== 'undefined') {
      console.warn(
        `[createModuleHttpClient] Missing X-Client-Id for ${moduleId}. ` +
          'Relay requests will fail with 400. Ensure ModuleProvider has loaded before making requests.'
      );
    }

    return config;
  });

  return Object.assign(client, {
    callWebhook: <TResponse = unknown>(webhookName: string, data: unknown) =>
      client.post<TResponse>(`/__webhooks/${webhookName}`, data),
  });
}
